import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import React, { PropsWithChildren, useMemo } from "react";
import { matchRoutes, RouteMatch, useLocation } from "react-router";

import {
  getAgendaItemId,
  getBoardId,
  getDocumentId,
  getMeetingId,
  getParticipantId,
  getRouteParams,
  getTaskId,
} from "./getRouteParams";
import { ParamsContext } from "./ParamsContext";
import { ParamsModel } from "./ParamsModel";
const logger = createLogger("ParamsProvider");

export const ParamsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { pathname } = useLocation();

  const contextValue = useMemo(() => {
    logger.debug(`ParamsProvider: pathname:=`, pathname);

    const routeMatches: RouteMatch[] | null = matchRoutes(
      [
        { path: AppRoutes.Home },
        { path: AppRoutes.Board },
        { path: AppRoutes.BoardSettings },
        { path: AppRoutes.BoardMeetings },
        { path: AppRoutes.BoardMeeting },
        { path: AppRoutes.BoardMeetingAgendaItems },
        { path: AppRoutes.BoardMeetingTasks },
        { path: AppRoutes.BoardMeetingDocuments },
      ],
      pathname
    );

    logger.debug(`ParamsProvider: routeMatches:=`, routeMatches);
    if (routeMatches === null) {
      return getRouteParams(pathname);
    }

    const match = routeMatches.find((x) => x.pathname === pathname);
    const params = match?.params || {};

    const matchPath = match?.route.path;
    logger.debug(`ParamsProvider: params:=`, params, ", route:=", matchPath);

    const boardId = getBoardId(undefined, params.boardId);
    const meetingId = getMeetingId(undefined, params.meetingId);
    const agendaItemId = getAgendaItemId(undefined, params.agendaItemId);
    const participantId = getParticipantId(undefined, params.participantId);
    const documentId = getDocumentId(undefined, params.documentId);
    const taskId = getTaskId(undefined, params.taskId);
    const isBoards = AppRoutes.Boards === matchPath;
    const isMeetings = AppRoutes.BoardMeetings === matchPath;
    const isMeeting = AppRoutes.BoardMeeting === matchPath;
    const isMeetingParticipants = AppRoutes.BoardMeetingParticipants === matchPath;
    const isMeetingAgendaItems = AppRoutes.BoardMeetingAgendaItems === matchPath;
    const isMeetingDocuments = AppRoutes.BoardMeetingDocuments === matchPath;
    const isMeetingOnedriveSync = AppRoutes.BoardMeetingOneDriveSync === matchPath;
    const isMeetingTasks = AppRoutes.BoardMeetingTasks === matchPath;

    const convertedParams: ParamsModel = {
      boardId,
      meetingId,
      agendaItemId,
      participantId,
      documentId,
      taskId,
      isBoards,
      isMeetings,
      isMeeting,
      isMeetingParticipants,
      isMeetingAgendaItems,
      isMeetingDocuments,
      isMeetingOnedriveSync,
      isMeetingTasks,
    };

    logger.debug(`ParamsProvider: convertedParams:=`, convertedParams);
    return convertedParams;

    // if i remove the extra deps, the compiler complains about
    // existing memoization cannot be preserved. Eslint bug?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, matchRoutes, getRouteParams]);

  // Derive entity ids directly from pathname instead of using state
  /*
  const contextValue = useMemo(() => {
    return getRouteParams(pathname);
  }, [pathname]);
  */

  return <ParamsContext.Provider value={contextValue}>{children}</ParamsContext.Provider>;
};
